import React, { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { send } from '../../services/chatMessages';
import SendIcon from '../../assets/send.svg';
import SendYellowIcon from '../../assets/send_yellow.svg';
import ImportantGray from '../../assets/important_gray.svg';
import ImportantRed from '../../assets/important_red.svg';
import AttachmentIcon from '../../assets/attachment.svg';
import { v4 as uuid4 } from 'uuid';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import DeleteRed from "../../assets/delete_red.svg";
import { LoadingMore } from "../Loading";
import { deserializeFile, getFileName } from "../../helpers/media";
import { formatDateTime } from "../../helpers/dates";
import { debounce } from "lodash";
import { useChatContext } from "../../context/chats";
import { ChatReplyToMessage } from "./ChatMessageItem";
import { ReplyToMessage } from "../../types/chatMessage";

interface Props {
    chatId?: string,
    messagesFetching: boolean,
    messageToReply: ReplyToMessage | null,
    onMessageSend: () => void,
    onTyping: () => void,
    onCancelReply: () => void,
}

export const ChatMessageSendForm = ({
    chatId,
    onMessageSend,
    messagesFetching,
    onTyping,
    messageToReply,
    onCancelReply
}: Props) => {
    const { drafts, updateDraftText, updateDraftFiles, updateDraft, clearDraft } = useChatContext();

    const [text, setText] = useState(drafts[chatId ?? '']?.text);
    const [isImportant, setIsImportant] = useState(!!drafts[chatId ?? '']?.isImportant);
    const [attachedFiles, setAttachedFiles] = useState<File[]>(() => {
        // Заполняем файлы для черновика, десериализуем их из строковых данных из localstorage
        const deserializedAttachments = drafts[chatId ?? '']?.attachments?.map(data => deserializeFile(data)) || [];
        return deserializedAttachments;
    });
    const [isEmptyMessage, setIsEmptyMessage] = useState<boolean>(true);
    const [inputKey, setInputKey] = useState(uuid4());
    const [loaderEnabled, setLoaderEnabled] = useState(false);
    const [messageSent, setMessageSent] = useState(false);

    const { mutate: sendMessage, isPending: isSendingMessage } = useMutation({
        mutationFn: send,
        onSuccess: (data) => {
            setText('');
            setInputKey(uuid4());
            setAttachedFiles([]);
            setMessageSent(true);
            setIsImportant(false);
            clearDraft(chatId ?? ''); // Очистка черновика
            onMessageSend();
        },
        onSettled: () => { // Попытка отправки была, независимо от результата
            setLoaderEnabled(false);
        }
    });

    // Обновление черновика чата
    // Файлы черновика
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            updateDraftFiles(chatId ?? '', attachedFiles);
        }, 1000); // Debounce time in milliseconds

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [attachedFiles]);
    // Текст и флаг "важно"
    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }
        debounceTimeout.current = setTimeout(() => {
            updateDraftText(chatId ?? '', text, isImportant);
        }, 1000); // Debounce time in milliseconds

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [text, isImportant]);

    useEffect(() => {
        if (messageSent && !messagesFetching) {
            // Сообщение отправлено и список сообщений не обновляется
            setLoaderEnabled(false);
        }
    }, [messageSent, messagesFetching]);

    const submit = () => {
        if (text || attachedFiles) {
            setLoaderEnabled(true); // Показываем лоадер
            if (chatId) {
                sendMessage({
                    chatId,
                    text: text ?? '',
                    isImportant,
                    files: attachedFiles,
                    repliedTo: messageToReply?.pk ?? null
                });
            }
        }
    }

    // Вложения
    const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const filesList = e.target.files;
        if (filesList) {
            const filesArray = Array.from(filesList);
            // Сохраняем только уникальные
            const uniqueFiles = filesArray.filter(file => !attachedFiles?.some(f => f.name === file.name));
            setAttachedFiles(prevState => [...prevState, ...uniqueFiles])
        }
    }

    const handleDeleteFile = (index: number) => {
        const newFiles = attachedFiles.filter((_, i) => i !== index);
        setAttachedFiles(newFiles);
    };

    // EMOJI PICKER START
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const handleEmojiClick = (emojiData: EmojiClickData) => {
        setText((prevMessage) => prevMessage + emojiData.emoji);
    };

    const handleEmojiMouseLeave = () => {
        setShowEmojiPicker(false);
    }
    // EMOJI PICKER STOP

    // Вставка из буфера обмена START
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const handlePaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
        const items = event.clipboardData.items;
        const fileList: File[] = [];

        // Обрабатываем данные буфера
        for (let i = 0; i < items.length; i++) {
            if (items[i].kind === 'file') {
                // Если файл, то получаем данные
                const file = items[i].getAsFile();
                if (file) {
                    fileList.push(file);
                }
            }
        }
        //
        if (fileList.length > 0) {
            // console.log(fileList);
            // Адаптируем данные из буфера обмена в файлы
            const dataTransfer = new DataTransfer();
            fileList.forEach(file => dataTransfer.items.add(file));
            const filesArray = Array.from(dataTransfer.files);
            // Сохраняем только уникальные
            const uniqueFiles = filesArray.filter(file => !attachedFiles.some(f => f.name === file.name && f.lastModified === file.lastModified));
            setAttachedFiles(prevState => [...prevState, ...uniqueFiles])
        }
    };
    // Вставка из буфера обмена STOP

    // Отправка события печатает с задержкой
    const [lastTypingTime, setLastTypingTime] = useState<number | null>(null);
    const timeout = 1000;
    const debouncedTyping = debounce(() => {
        onTyping();
    }, timeout);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Проверка возможности отправки ивента
        const now = Date.now();
        if (!lastTypingTime || now - lastTypingTime >= timeout) {
            setLastTypingTime(now);
            debouncedTyping();
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            submit();
        }
    };

    const handleIsImportant = () => {
        setIsImportant(!isImportant);
    }

    // Многострочный ввод START
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [textareaRows, setTextareaRows] = useState(2);
    const [roundedClass, setRoundedClass] = useState('rounded-r-lg');
    const [textareaHeight, setTextareaHeight] = useState(0);
    const _maxRows = 6; // Макс число строк в textarea
    useEffect(() => {
        const textarea = textareaRef.current;
        if (textarea) {
            textarea.style.height = 'auto'; // Сброс высоты

            const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight, 14);
            const rows = Math.ceil(textarea.scrollHeight / lineHeight);
            let _rows = rows > _maxRows ? _maxRows : rows;

            if (_rows > 2) {
                setRoundedClass('rounded-r-lg');
            }
            if (!text) {
                _rows = 2;
                setRoundedClass('rounded-r-lg');
            }

            const newHeight = _rows * lineHeight;
            setTextareaHeight(newHeight);
            textarea.style.height = `${newHeight}px`;
        }
    }, [text]);
    // Многострочный ввод STOP

    // Отслеживаем введенный текст и прикреплённые файлы
    useEffect(() => {
        let isEmpty = true;
        if (!!attachedFiles?.length) {
            isEmpty = false;
        }
        if (!!text) {
            isEmpty = false
        }
        setIsEmptyMessage(isEmpty)
    }, [text, attachedFiles]);

    return (
        <div className='flex flex-col' onPaste={handlePaste}>
            {/*REPLY TO MESSAGE*/}

            {messageToReply &&
                <div className='w-full h-full max-h-10 mt-2 flex justify-center items-center'>
                    <ChatReplyToMessage
                        messageToReply={messageToReply}
                        onCancelReply={onCancelReply}
                        showCancelButton={true}
                    />
                </div>
            }
            {/*ATTACHED FILES*/}
            <div className="flex flex-col justify-center items-start max-h-32 overflow-y-auto gap-1 py-2">
                <>{!isSendingMessage && attachedFiles &&
                    attachedFiles.map((e, index) =>
                        <div
                            key={index}
                            className="!max-h-10 px-2 py-1 flex flex-1 flex-row justify-start items-center w-full rounded-lg bg-melon-light">
                            <div className="text-md">
                                {getFileName(e.name, 64)}
                            </div>
                            <div className="text-xs text-melon-grey ml-auto">
                                файл изменен {formatDateTime(new Date(e.lastModified).toString())}
                            </div>
                            <button
                                className="cursor-pointer ml-2 flex-[0_0_auto]"
                                onClick={(event) => {
                                    handleDeleteFile(index)
                                }}
                            >
                                <img alt="" src={DeleteRed} className="!relative !w-6 !h-6" />
                            </button>
                        </div>
                    )}
                </>
            </div>
            {/*Лоадер*/}
            {loaderEnabled && <div className="py-2 relative flex flex-col justify-center items-start">
                <LoadingMore />
            </div>}
            {/*INPUT ITEMS*/}
            <div className='flex flex-row mb-5 items-end justify-around'>
                {/*ATTACH FILES ICON*/}
                <div
                    className="
                    flex justify-center items-center
                    relative
                    bg-melon-light p-3 rounded-l-lg
                    w-16 h-16
                    "
                    style={{
                        height: `${textareaHeight}px`
                    }}
                >
                    <label
                        htmlFor="upload-chat-files"
                        className="
                            relative
                            cursor-pointer
                            inline-flex
                            justify-center items-center
                            w-full
                            h-full
                        "
                    >
                        <img alt="" src={AttachmentIcon} className="!relative !w-6 !h-6" />
                    </label>
                    <input
                        id="upload-chat-files"
                        type="file"
                        onChange={handleFilesChange}
                        ref={fileInputRef}
                        key={inputKey}
                        multiple
                        className="hidden">
                    </input>
                </div>
                {/*TEXT*/}
                <div
                    className={'relative !mih-h-16 mr-2 grow flex flex-row justify-center items-center ' + roundedClass}
                >
                    <button
                        onClick={handleIsImportant}
                        className='absolute right-2 bottom-4 bg-white rounded-lg h-8 w-[100px] flex flex-row justify-center gap-1 items-center '
                    >
                        {isImportant && <img src={ImportantRed} alt="important red" />}
                        {!isImportant && <img src={ImportantGray} alt="important gray" />}
                        Важно
                    </button>
                    <textarea
                        rows={textareaRows}
                        ref={textareaRef}
                        className={'resize-none bg-melon-light w-full pt-4 px-4 border-melon-grey pr-[112px] focus:outline-none  h-full ' + roundedClass}
                        placeholder='Напишите сообщение'
                        onChange={e => setText(e.target.value)}
                        value={text}
                        onKeyDown={handleKeyDown}
                    />
                </div>
                {/*EMOJI*/}
                <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="
                    bg-melon-light
                    px-4 py-4
                    !h-16
                    rounded-lg
                    focus:outline-none
                    mr-2
                    "
                >
                    😊
                </button>
                {showEmojiPicker && (
                    <div
                        onMouseLeave={handleEmojiMouseLeave}
                        className="fixed bottom-32 right-32 z-20">
                        <EmojiPicker onEmojiClick={handleEmojiClick} />
                    </div>
                )}
                {/*SEND*/}

                <button
                    onClick={submit}
                    disabled={isSendingMessage || isEmptyMessage}
                >
                    {
                        (isSendingMessage || isEmptyMessage) ? <img
                            className='bg-melon-light !h-16 rounded-lg inline-block'
                            src={SendIcon}
                            alt='Sending message'
                        /> : <img
                            className='bg-melon-light !h-16 rounded-lg inline-block cursor-pointer'
                            src={SendYellowIcon}
                            alt='Send message'
                        />
                    }
                </button>
            </div>
        </div>
    );
};
